import Layout from '../components/Layout';
import { PRODUCTS } from '../mock';
import TagVisual from '../components/TagVisual';
import { Check, ShoppingCart, ArrowRight, Tag as TagIcon, X, Zap } from 'lucide-react';
import { addToCart } from '../components/CartSheet';
import { toast } from '../hooks/use-toast';
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const ref = params.get('ref');
  const [referralInfo, setReferralInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ref) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/referral/${ref}`);
        setReferralInfo(data);
        localStorage.setItem('referral_code', data.code);
      } catch {
        setReferralInfo({ invalid: true });
      }
    })();
  }, [ref]);

  const clearReferral = () => {
    localStorage.removeItem('referral_code');
    setReferralInfo(null);
    const p = new URLSearchParams(params);
    p.delete('ref');
    setParams(p);
  };

  const buy = (p) => {
    addToCart(p);
    toast({ title: 'Added to cart', description: `${p.name} · ₹${p.price}` });
  };

  const buyNow = (p) => {
    addToCart(p);
    // Open cart sheet by dispatching a custom event captured by Navbar? Simpler: navigate to shop then let user open cart via nav.
    // Simpler still: just navigate to shop with cart open param — but we don't have that. Best: dispatch event.
    window.dispatchEvent(new Event('cart:open'));
  };

  return (
    <Layout>
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          {referralInfo && !referralInfo.invalid && (
            <div className="mb-8 card-soft p-4 flex justify-between items-center bg-navy text-white border-navy">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><TagIcon size={16} /></div>
                <div>
                  <p className="text-sm font-semibold">Partner offer applied</p>
                  <p className="text-xs text-blue-200">Referred by {referralInfo.partner_name} ({referralInfo.partner_city}) · Code {referralInfo.code}</p>
                </div>
              </div>
              <button onClick={clearReferral} className="p-1.5 rounded-full hover:bg-white/10"><X size={14} /></button>
            </div>
          )}
          {referralInfo?.invalid && (
            <div className="mb-8 card-soft p-4 text-sm text-red-600 flex justify-between items-center">
              Referral code not recognised. Please check with your partner.
              <button onClick={clearReferral} className="p-1.5 rounded-full hover:bg-black/5"><X size={14} /></button>
            </div>
          )}

          <p className="eyebrow text-center mb-4">Shop</p>
          <h1 className="h-display text-4xl md:text-6xl text-center">Pick your <em className="font-serif-display italic font-normal text-navy">Connect</em> plan.</h1>
          <p className="mt-4 text-center text-neutral-600 max-w-xl mx-auto">Free delivery pan-India. COD available. Ships within 24 hours.</p>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {PRODUCTS.map((p, i) => {
              const savings = (p.mrp || p.original || p.price) - p.price;
              const pct = p.mrp ? Math.round((savings / p.mrp) * 100) : 0;
              return (
              <div key={p.id} className={`rounded-2xl p-6 flex flex-col hover:-translate-y-1 transition-transform shadow-xl text-white ${i === 1 ? 'ring-2 ring-orange-400' : ''}`} style={{background: 'linear-gradient(150deg, #0B2E6F 0%, #071E4A 100%)'}} data-testid={`product-card-${p.id}`}>
                {p.tag && (
                  <span className="self-start text-[11px] tracking-wider uppercase font-semibold px-2.5 py-1 rounded-full bg-white/15 text-yellow-200">{p.tag}</span>
                )}
                <div className="my-6 aspect-[8/5] flex items-center justify-center overflow-hidden bg-white/95">
                  <TagVisual size={400} imageClassName="border border-white rounded-sm" />
                </div>
                <h3 className="text-2xl font-semibold text-white">{p.name}</h3>
                <p className="text-sm text-blue-200 mt-1">{p.tagline}</p>
                <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                  <span className="text-3xl font-bold text-white">₹{p.price}</span>
                  {p.mrp && <span className="text-sm text-blue-300 line-through">MRP ₹{p.mrp}</span>}
                  {pct > 0 && <span className="text-[11px] font-semibold text-green-200 bg-green-400/20 px-2 py-0.5 rounded-full">{pct}% OFF</span>}
                </div>
                {savings > 0 && <p className="text-xs text-green-300 font-medium mt-1">You save ₹{savings}</p>}
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="text-sm text-blue-100 flex items-start gap-2"><Check size={14} className="text-orange-300 flex-shrink-0 mt-0.5" /> <span>{f}</span></li>
                  ))}
                </ul>
                <div className="mt-6 space-y-2">
                  <button onClick={() => buy(p)} className="w-full justify-center inline-flex items-center gap-2 rounded-full py-2.5 px-4 font-semibold text-sm bg-white text-navy hover:bg-blue-50 transition-colors" data-testid={`add-cart-${p.id}`}>
                    <ShoppingCart size={16} /> Add to cart
                  </button>
                  <button onClick={() => buyNow(p)} className="w-full justify-center inline-flex items-center gap-2 rounded-full py-2.5 px-4 font-semibold text-sm bg-orange-500 text-white hover:bg-orange-400 transition-colors" data-testid={`buy-now-${p.id}`}>
                    <Zap size={16} /> Buy now
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          <div className="mt-12 rounded-3xl p-6 md:p-8 text-white text-center shadow-2xl relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0B2E6F 0%, #071E4A 55%, #030A1F 100%)'}} data-testid="shop-promo-banner">
            <div className="absolute -left-16 -top-16 w-64 h-64 rounded-full bg-orange-500/15"></div>
            <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-orange-500/10"></div>
            <div className="relative">
              <p className="text-xs uppercase tracking-widest font-bold text-orange-300 mb-2">🎁 What's inside every tag</p>
              <h3 className="h-display text-2xl md:text-3xl leading-tight max-w-3xl mx-auto text-white">More than just a QR Tag &mdash; <em className="font-serif-display italic font-normal text-orange-300">Complete On-Road Safety Management!</em></h3>
              <p className="mt-3 text-base md:text-lg text-blue-100 max-w-2xl mx-auto">Get <span className="font-bold text-white">1 Year Complimentary Access</span> to all premium features.</p>
            </div>
          </div>

          <div className="mt-10 rounded-3xl p-8 md:p-10 grid md:grid-cols-3 gap-6 items-center text-white shadow-xl" style={{background: 'linear-gradient(150deg, #0B2E6F 0%, #071E4A 100%)'}} data-testid="bulk-order-card">
            <div className="md:col-span-2">
              <h3 className="h-display text-2xl md:text-3xl text-white">Bulk Order for Society, Fleet &amp; Company</h3>
              <p className="mt-2 text-blue-100">Get up to 30% off on orders above 50 tags.</p>
            </div>
            <a href="/partner" className="inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-semibold bg-white text-navy hover:bg-blue-50 transition-colors" data-testid="bulk-order-cta">Become a partner <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
